/**
 * Shared SEO helpers for public-facing routes.
 *
 * Usage in a route:
 *   import { buildSeoMeta } from '~/utils/seo';
 *
 *   export const meta: MetaFunction = () =>
 *     buildSeoMeta({
 *       title: 'Find a Dentist Near You | ToothReferral',
 *       description: 'Search qualified dentists...',
 *       path: '/find-a-dentist',
 *     });
 */

const SITE_URL = 'https://toothreferral.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.png`;

interface SeoMetaInput {
  title: string;
  description: string;
  /** Path without domain, e.g. '/find-a-dentist'. Use '/' for homepage. */
  path: string;
  /** Override the default OG image URL. */
  ogImage?: string;
}

export function buildSeoMeta({ title, description, path, ogImage }: SeoMetaInput) {
  const canonicalUrl = `${SITE_URL}${path === '/' ? '' : path}`;
  const image = ogImage ?? DEFAULT_OG_IMAGE;

  return [
    { title },
    { name: 'description', content: description },
    // Canonical
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    // Open Graph
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: image },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'ToothReferral' },
    // Twitter / X
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image },
  ];
}
